import React, { useCallback, useEffect, useMemo, useState, useRef, Fragment } from "react";
import { Link } from "react-router-dom";

import COMMON from "common";
import api from "services/api";
import pathnames from "routes/pathnames";
import classNames from "common/class-names";
import { isSameDate, formatDatePattern, getDaysDifference } from "common/calendar";
import serveRequestErrors from "common/serve-request-errors";
import AppChevronIcon from "components/icons/app-chevron-icon";
import AppCalendarCheckIcon from "components/icons/app-calendar-check-icon";
import AppReceiptIcon from "components/icons/app-receipt-icon";
import AppBookCheckIcon from "components/icons/app-book-check-icon";
import AppAnnouncementDescriptionModal from "components/pages/dashboard/app-announcement-description-modal";
import pdfIcon from "assets/images/pdf-icon.svg";
import holidayIcon from "assets/images/holiday-icon.svg";
import AppEmptyState from "components/app-empty-state";

const formatAnnouncementDate = (date) => {
	if (!date) return "-";

	const days = getDaysDifference(date, new Date());

	if (days === 0) return "Today";

	if (days === 1) return "1 day ago";

	if (days > 1 && days <= 7) return days + " days ago";

	return formatDatePattern(date, "DD/MM/YYYY");
};

const getAnnouncementFile = (announcement) => announcement.hrAnnouncementFile?.filePath || announcement.file;

const formatLeaveTypeLabel = (leaveType) => (leaveType || "").replace(/_/g, " ").toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());

const getLeaveTypeModifier = (leaveType) => (leaveType || "").toLowerCase().replace(/_/g, "-");

const getLeaveShortForm = (leaveType) => {
	const shortForm = COMMON.BENEFITS.LEAVE_SHORT_FORMS[leaveType];

	if (shortForm) return shortForm;

	// unknown leave types built from the label initials
	return formatLeaveTypeLabel(leaveType).split(" ").map((o) => o.charAt(0)).join("").slice(0, 3).toUpperCase() || "-";
};

// Leave Balance Colour Code
const getLeaveBalanceLevel = (balanceDays) => {
	const days = Number(balanceDays) || 0;

	if (days >= 12) return "high";

	if (days >= 6) return "medium";

	return "low";
};

const LeaveTypeBadge = ({ leaveType }) => {
	//prettier-ignore
	const className = useMemo(() => classNames({ "leave-badge": true, ["leave-badge--" + getLeaveTypeModifier(leaveType)]: !!leaveType }), [leaveType]);

	return (
		<div className={className}>
			<p className="leave-badge__text">{getLeaveShortForm(leaveType)}</p>
		</div>
	);
};

const formatLeaveRange = (startDate, endDate) => {
	const start = new Date(startDate);
	const end = new Date(endDate);
	const currentYear = new Date().getFullYear();
	const dayMonthFormat = start.getFullYear() !== currentYear || end.getFullYear() !== currentYear ? "D MMM YYYY" : "D MMM";

	// Same day, show only the start date
	if (isSameDate(start, end)) return formatDatePattern(start, dayMonthFormat);

	const isSameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();

	return formatDatePattern(start, isSameMonth ? "D" : dayMonthFormat) + " – " + formatDatePattern(end, dayMonthFormat);
};

// Countdown Calculator
const formatLeaveCountdown = (startDate) => {
	const days = getDaysDifference(new Date(), startDate);

	if (days > 1) return { label: "In " + days + " days", isToday: false };

	if (days === 1) return { label: "Tomorrow", isToday: false };

	if (days === 0) return { label: "Today", isToday: true };

	return { label: "Ongoing", isToday: false };
};

// the badge already carries the short form, so the leave type gives way to the duration when the row runs out of space
const UpcomingLeaveName = ({ label, timeOff }) => {
	const infoRef = useRef();
	const measureRef = useRef();
	const [isCompact, setIsCompact] = useState(false);

	const onHandleContent = (isMeasure) => (
		<Fragment>
			{(isMeasure || !isCompact) && <p className="leaves__name">{label}</p>}

			{!!timeOff && <span className="leaves__duration">{timeOff}</span>}
		</Fragment>
	);

	useEffect(() => {
		const info = infoRef.current;
		const measure = measureRef.current;

		if (!info || !measure || !timeOff) return;

		const onHandleMeasure = () => setIsCompact(measure.offsetWidth > info.clientWidth);

		onHandleMeasure();

		const observer = new ResizeObserver(onHandleMeasure);

		observer.observe(info);

		return () => observer.disconnect();
	}, [label, timeOff]);

	return (
		<div className="leaves__info" ref={infoRef} title={timeOff ? label + " - " + timeOff : label}>
			<div className="leaves__type">{onHandleContent(false)}</div>

			<div className="leaves__measure" ref={measureRef} aria-hidden="true">
				{onHandleContent(true)}
			</div>
		</div>
	);
};

const PageDashboard = (props) => {
	const announcementDescriptionRef = useRef();
	const [data, setData] = useState({});
	const cancelRequest = useMemo(() => props.onHandleCancelRequest, [props.onHandleCancelRequest]);
	//prettier-ignore
	const pendings = useMemo(() => [
		{ accent: "green", label: "Pending Leave", count: data.pendingLeaveCount || 0, pathname: pathnames.leavesPending, icon: <AppCalendarCheckIcon /> },
		{ accent: "purple", label: "Pending Claim", count: data.pendingClaimCount || 0, pathname: pathnames.claimPending, icon: <AppReceiptIcon /> },
		{ accent: "orange", label: "Ongoing Assignment", count: data.pendingOngoingAssignmentCount || 0, pathname: pathnames.ongoingAssigment, icon: <AppBookCheckIcon /> },
	], [data]);

	const onHandleReadMoreDescription = useCallback((announcement) => {
		announcementDescriptionRef.current.onHandleShow({ title: announcement.title, description: announcement.description });
	}, []);

	//prettier-ignore
	const Announcements = useCallback((obj) => {
		const list = [...(obj.announcements || [])].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));

		return (
			<ul className="announcements announcements--scrollable">
				{list.length > 0 ? (
					list.map((o) => {
						const file = getAnnouncementFile(o);

						return (
							<li key={o.id} className="announcements__item" role="button" tabIndex={0} onClick={() => obj.onHandleReadMoreDescription(o)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); obj.onHandleReadMoreDescription(o); } }}>
								<div className="announcements__content">
									<div className="announcements__heading">
										<span className="announcements__dot" />
										<p className="announcements__title">{o.title}</p>
									</div>

									<p className="announcements__meta">
										{!!o.startDate && (
											<Fragment>
												<span className="announcements__date">{formatAnnouncementDate(o.startDate)}</span>
												<span className="announcements__separator" aria-hidden="true">·</span>
											</Fragment>
										)}
										<span className="announcements__summary">{o.description}</span>
									</p>
								</div>

								<div className="announcements__actions" onClick={(event) => event.stopPropagation()}>
									{file && (
										<a className="announcements__button" href={file} target="_blank" rel="noreferrer">
											<img src={pdfIcon} alt="attachement" />
										</a>
									)}
								</div>
							</li>
						);
					})
				) : (
					<AppEmptyState
						title="No records found"
						description="You don’t have any records yet"
						disabledButton
					/>
				)}
			</ul>
		);
	}, []);

	const formatDate = (date) => {
		const today = new Date().getDate();
		const day = new Date(date).getDate();

		if (today === day) return "Today";

		if (day > 3 && day < 21) return day + "th";

		switch (day % 10) {
			case 1:
				return day + "st";
			case 2:
				return day + "nd";
			case 3:
				return day + "rd";
			default:
				return day + "th";
		}
	};

	// a holiday can span days now, so show the range when endDate differs
	const formatHolidayDate = (holiday) => {
		if (!holiday.endDate || isSameDate(holiday.startDate, holiday.endDate)) return formatDate(holiday.startDate);

		return formatDate(holiday.startDate) + " - " + formatDate(holiday.endDate);
	};

	useEffect(() => {
		const initialize = async () => {
			let response = null;

			try {
				response = await api.get.dashboard.employee();
			} catch (error) {
				serveRequestErrors(error);
			}

			if (response) setData(response);
		};

		initialize();
	}, []);

	useEffect(() => {
		return () => {
			cancelRequest(COMMON.ENDPOINT_PATH.DASHBOARD.EMPLOYEE);
		};
	}, [cancelRequest]);

	return (
		<div className="page-dashboard">
			<div className="dashboard">
				<div className="dashboard__section dashboard__sectionTop">
					<div className="dashboard__column">
						<div className="card card--announcements">
							<div className="card__header">
								<p className="card__title">Latest Announcements</p>
							</div>

							<Announcements announcements={data.announcements} onHandleReadMoreDescription={onHandleReadMoreDescription} />
						</div>
					</div>

					<div className="dashboard__column dashboard__column--aside">
						<div className="card card--holiday-this-month">
							<div className="card__header">
								<p className="card__title">Holiday this Month</p>
							</div>

							<ul className="celebrations">
								{data.holidays?.length ? (
									data.holidays.map((o, i) => {
										return (
											<li key={i} className="celebrations__item">
												<img className="celebrations__icon" src={holidayIcon} alt="holiday" />

												<p className="celebrations__name" title={o.title}>{o.title}</p>

												<p className="celebrations__date">{formatHolidayDate(o)}</p>
											</li>
										);
									})
								) : (
									<AppEmptyState description="No holiday this month" disabledButton disabledImage />
								)}
							</ul>
						</div>

						<div className="card card--pendings">
							<div className="pendings">
								{pendings.map((o, i) => {
									const itemClassName = classNames({ pendings__item: true, ["pendings__item--" + o.accent]: true });

									return (
										<Fragment key={o.label}>
											{i > 0 && <div className="pendings__divider" />}

											<div className={itemClassName}>
												<div className="pendings__icon">{o.icon}</div>

												<div className="pendings__content">
													<p className="pendings__title">{o.label}</p>

													<Link className="card__link" to={o.pathname}>
														<div className="dashboard__icon">
															<p className="dashboard__label">View All</p>
															<AppChevronIcon />
														</div>
													</Link>
												</div>

												<div className="pendings__balance">{o.count}</div>
											</div>
										</Fragment>
									);
								})}
							</div>
						</div>
					</div>
				</div>

				<div className="dashboard__section dashboard__sectionBottom">
					<div className="card card--celebration-corner">
						<div className="card__header">
							<p className="card__title">Celebration Corner</p>
						</div>

						<ul className="celebrations">
							{data.birthdayEmployees?.length ? (
								data.birthdayEmployees.map((o, i) => {
									return (
										<li key={i} className="celebrations__item">
											<div className="celebrations__profile" style={{ backgroundImage: `url(${o.avatar})` }} />

											<p className="celebrations__name" title={o.name}>{o.name}</p>

											<p className="celebrations__date">{formatDate(o.date)}</p>
										</li>
									);
								})
							) : (
								<AppEmptyState description="No celebrations this month" disabledButton disabledImage />
							)}
						</ul>
					</div>

					<div className="card card--upcoming-leave">
						<div className="card__header">
							<p className="card__title">Upcoming Leave</p>

							<Link className="card__link" to={pathnames.leavesUpcoming}>
								<div className="dashboard__icon">
									<p className="dashboard__label">View All</p>
									<AppChevronIcon />
								</div>
							</Link>
						</div>

						<ul className="leaves">
							{data.upcomingLeaves?.length ? (
								data.upcomingLeaves.map((o, i) => {
									const onLeaveLength = o.companyLeaveApplicationDetails.length;
									const onLeaveStart = o.companyLeaveApplicationDetails[0].date;
									const onLeaveEnd = o.companyLeaveApplicationDetails[onLeaveLength - 1].date;
									const countdown = formatLeaveCountdown(onLeaveStart);
									const countdownClassName = classNames({ leaves__countdown: true, "leaves__countdown--today": countdown.isToday });
									const dates = formatLeaveRange(onLeaveStart, onLeaveEnd);

									return (
										<li key={i} className="leaves__item">
											<LeaveTypeBadge leaveType={o.leaveType} />

											<UpcomingLeaveName label={o.leaveTypeMapped || formatLeaveTypeLabel(o.leaveType)} timeOff={o.timeOff?.label} />

											<div className="leaves__date">
												<p className={countdownClassName}>{countdown.label}</p>
												<p className="leaves__range">{dates}</p>
											</div>
										</li>
									);
								})
							) : (
								<AppEmptyState description="No upcoming leave found" disabledButton disabledImage />
							)}
						</ul>
					</div>

					<div className="card card--leave-balance">
						<div className="card__header">
							<p className="card__title">Leave Balance</p>

							<Link className="card__link" to={pathnames.leavesSummary}>
								<div className="dashboard__icon">
									<p className="dashboard__label">View All</p>
									<AppChevronIcon />
								</div>
							</Link>
						</div>

						<ul className="leaves">
							{data.leaveBalances?.length ? (
								data.leaveBalances.map((o, i) => {
									//prettier-ignore
									const balanceClassName = classNames({ leaves__balance: true, ["leaves__balance--" + getLeaveBalanceLevel(o.balanceDays)]: true });

									return (
										<li key={i} className="leaves__item">
											<LeaveTypeBadge leaveType={o.leaveType} />

											<p className="leaves__name">{o.leaveTypeMapped || formatLeaveTypeLabel(o.leaveType)}</p>

											<p className={balanceClassName}>{o.balanceDays} days</p>
										</li>
									);
								})
							) : (
								<AppEmptyState description="No leave record found" disabledButton disabledImage />
							)}
						</ul>
					</div>
				</div>

			</div>

			<AppAnnouncementDescriptionModal ref={announcementDescriptionRef} />
		</div>
	);
};

export default PageDashboard;
